import React, { useState, useRef, useEffect } from 'react';
import { StatusMessage } from '../ui';
import { FormField } from '../ui/FormField';
import { DatePickerField } from '../ui/DatePickerField';
import { useFormValidation } from '../../hooks/useFormValidation';
import { validators } from '../../helpers/validators';
import { FormErrorSummary } from '../ui/FormErrorSummary';

interface TimeSlot {
    id?: number;
    dayOfWeek: number;
    // When true, this timeslot applies to a specific calendar date instead of a day-of-week
    isDate?: boolean;
    // ISO date YYYY-MM-DD when isDate is true
    date?: string;
    dayOfWeekName?: string;
    startTime: string;
    endTime: string;
    isActive: boolean;
}

interface ScheduleFormData {
    name: string;
    description?: string;
    isActive: boolean;
    timeSlots: TimeSlot[];
}

interface SchedulesFormProps {
    onSubmit: (data: ScheduleFormData) => void | Promise<void>;
    onCancel?: () => void;
    initialData?: Partial<ScheduleFormData>;
    isLoading?: boolean;
    // Optional external ref to the form element so parent/modal can call `requestSubmit()`
    formRef?: React.RefObject<HTMLFormElement | null>;
    // Hide the internal submit button when the modal's OK button should submit the form
    hideSubmitButton?: boolean;
}

const daysOfWeek = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' }
];

export const SchedulesForm: React.FC<SchedulesFormProps> = ({
    onSubmit,
    onCancel,
    initialData,
    isLoading = false,
    formRef,
    hideSubmitButton = false,
}) => {
    // Determine edit mode from whether initialData was provided by the parent
    const isEditMode = Boolean(initialData && (initialData.name || (initialData.timeSlots && initialData.timeSlots.length > 0)));
    const [showErrorSummary, setShowErrorSummary] = useState(false);

    const [formData, setFormData] = useState<ScheduleFormData>({
        name: initialData?.name || '',
        description: initialData?.description || '',
        isActive: initialData?.isActive ?? true,
        timeSlots: initialData?.timeSlots || []
    });

    const { values, errors, handleChangeFormField, validateAll } = useFormValidation<{ name: string; timeSlots?: TimeSlot[] }>({
        initialValues: [{ name: initialData?.name || '' }],
        rules: {
            name: [
                validators.required('Schedule name is required'),
                validators.minLength(2, 'Schedule name must be at least 2 characters'),
                validators.maxLength(100, 'Schedule name must be less than 100 characters'),
                validators.pattern(/^[A-Za-z0-9 ]+$/,'Only letters, numbers, and spaces are allowed for Schedule name')
            ]
        }
    });

    // Ref used to scroll the name validation error into view when it appears
    const nameErrorRef = useRef<HTMLDivElement | null>(null);
    const nameError = errors?.[0]?.name;

    useEffect(() => {
        if (nameError) {
            // Smoothly scroll the error banner into center of view
            try {
                nameErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } catch (e) {
                console.info('scrollIntoView failed', e);
            }
        }
    }, [nameError]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleAddTimeSlot = () => {
        setFormData(prev => ({
            ...prev,
            timeSlots: [
                ...prev.timeSlots,
                {
                    dayOfWeek: 1,
                    isDate: false,
                    date: '',
                    startTime: '09:00:00',
                    endTime: '17:00:00',
                    isActive: true
                }
            ]
        }));
        
        let valid = validateAll([{ name: formData.name, timeSlots: formData.timeSlots }]);
        valid = valid.filter(item => !item.hasOwnProperty("timeSlots"));
        let hasErrors = false;
        
        if (Array.isArray(valid)) {
            hasErrors = valid.some(v => v && Object.keys(v).length > 0);
        } else if (valid && typeof valid === 'object') {
            hasErrors = Object.keys(valid).length > 0;
        }

        if (hasErrors) {
            setShowErrorSummary(true);
        }
        else {
            setShowErrorSummary(false);
        }
    };

    const handleTimeSlotChange = (index: number, field: keyof TimeSlot, value: string | number | boolean) => {
        setFormData(prev => ({
            ...prev,
            timeSlots: prev.timeSlots.map((slot, i) => 
                i === index ? { ...slot, [field]: value } : slot
            )
        }));
    };

    const handleDeleteTimeSlot = (index: number) => {
        setFormData(prev => ({
            ...prev,
            timeSlots: prev.timeSlots.filter((_, i) => i !== index)
        }));
        
        const valid = validateAll([{ name: formData.name, timeSlots: formData.timeSlots }]);

        if (formData.timeSlots.length === 1){
            valid.push({ timeSlots: "Timeslot is required" });
        }

        let hasErrors = false;

        if (Array.isArray(valid)) {
            hasErrors = valid.some(v => v && Object.keys(v).length > 0);
        } else if (valid && typeof valid === 'object') {
            hasErrors = Object.keys(valid).length > 0;
        }
        else {
            console.info('Unexpected validation result:', valid);
        }

        if (hasErrors) {
            setShowErrorSummary(true);
        }
        else {
            setShowErrorSummary(false);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (isLoading) return;

        // Validate schedule name before submitting
        const valid = validateAll([{ name: formData.name, timeSlots: formData.timeSlots }]);

        if (!formData.timeSlots.length){
            valid.push({ timeSlots: "Timeslot is required" });
        }

        let hasErrors = false;
        if (Array.isArray(valid)) {
            hasErrors = valid.some(v => v && Object.keys(v).length > 0);
        } else if (valid && typeof valid === 'object') {
            hasErrors = Object.keys(valid).length > 0;
        }
        else{
            console.info('Unexpected validation result:', valid);
        }

        if (hasErrors) {
            setShowErrorSummary(true);
            // Do not submit if validation failed; errors are shown inline
            return;
        }

        onSubmit(formData);
    };

    const getDayName = (dayOfWeek: number): string => {
        return daysOfWeek.find(d => d.value === dayOfWeek)?.label || 'Unknown';
    };

    const handleCloseStatusMessage = () => {
         setShowErrorSummary(false);
    };

    return (
        <div className='mb-2'>
            <form ref={formRef} onSubmit={handleSubmit} className="form">
                <h3 className="heading__title heading__title--schedules">
                    {isEditMode ? 'Edit Schedule' : 'Add Schedule'}
                </h3>

                <div className="form__group mt-2">
                    {showErrorSummary &&
                        errors.length > 0 &&
                        [...new Set(
                            errors.flatMap(err => Object.values(err).filter(Boolean))
                        )].length > 0 && (
                            <StatusMessage kind="error" onClose={handleCloseStatusMessage}>
                                <FormErrorSummary
                                    errors={[...new Set(
                                        errors.flatMap(err => Object.values(err).filter(Boolean))
                                    )] as string[]}
                                />
                            </StatusMessage>
                        )
                    }
                    <label htmlFor="name" className="form__label form__label--required">Schedule Name</label>
                    <FormField
                        placeholder="Enter schedule name"
                        name="name"
                        type="text"
                        value={formData.name}
                        enableCopyPaste={false}
                        onChange={(e) => {
                            handleChangeFormField(0, 'name')(e);
                            handleChange(e);
                        }}
                        disabled={isLoading}
                        error={errors[0]?.name}
                        showErrorMessage={false}
                    />
                </div>

                <div className="form__group">
                    <label htmlFor="description" className="form__label">Description</label>
                    <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        disabled={isLoading}
                        className="form__control"
                        placeholder="Enter schedule description"
                        rows={3}
                    />
                </div>

                {/* isActive is kept in form state but not editable via the UI; schedules are active by default */}

                <div className="form__section">
                    <h4 className="form__subtitle">Time Slots</h4>

                    {formData.timeSlots.length > 0 ? (
                        <>
                            {formData.timeSlots.map((slot, index) => (
                                <div key={slot.id || `timeslot-${index}-${slot.dayOfWeek}-${slot.startTime}-${slot.date}`} className="form-entry form-entry--column mb-2">
                                    <div className="form-entry__heading">
                                        <h3 className="heading__title--building">Time Slot {index + 1} {slot.isDate ? `— ${slot.date}` : `— ${getDayName(slot.dayOfWeek)}`}</h3>
                                        <button
                                            type="button"
                                            className="btn__close se-icon--close"
                                            onClick={() => handleDeleteTimeSlot(index)}
                                            disabled={isLoading}
                                            aria-label={`Remove time slot ${index + 1}`}
                                        />
                                    </div>

                                    <div className="form-row__select mb-1">
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <input
                                                type="checkbox"
                                                checked={!!slot.isDate}
                                                onChange={e => {
                                                    const checked = e.target.checked;
                                                    handleTimeSlotChange(index, 'isDate', checked);
                                                    if (checked && !slot.date) {
                                                        const today = new Date().toISOString().split('T')[0];
                                                        handleTimeSlotChange(index, 'date', today);
                                                    }
                                                    if (!checked) {
                                                        handleTimeSlotChange(index, 'date', '');
                                                    }
                                                }}
                                                disabled={isLoading}
                                            />
                                            <span>Specific date</span>
                                        </label>
                                    </div>

                                    <div className="form-row">
                                        {!slot.isDate ? (
                                            <div className="form-row">
                                                <label className="form__label">Day of Week</label>
                                                <select
                                                    value={slot.dayOfWeek}
                                                    onChange={e => handleTimeSlotChange(index, 'dayOfWeek', Number(e.target.value))}
                                                    disabled={isLoading}
                                                    className="form__control select__control"
                                                >
                                                    {daysOfWeek.map(day => (
                                                        <option key={day.value} value={day.value}>{day.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        ) : (
                                            <div className="form-row__row">
                                                <DatePickerField
                                                    label="Date"
                                                    name={`date-${index}`}
                                                    value={slot.date || ''}
                                                    onChange={value => handleTimeSlotChange(index, 'date', value)}
                                                    disabled={isLoading}
                                                />
                                            </div>
                                        )}
                                        <div className='form-row--row'>
                                            <div className='col-6-xs'>
                                                <div className="form-row__field">
                                                    <label className="form__label">Start</label>
                                                    <input
                                                        type="time"
                                                        value={slot.startTime.substring(0, 5)}
                                                        onChange={e => handleTimeSlotChange(index, 'startTime', e.target.value + ':00')}
                                                        disabled={isLoading}
                                                        className="form__control"
                                                    />
                                                </div>
                                            </div>
                                            <div className='col-6-xs'>
                                                <div className="form-row__field">
                                                    <label className="form__label">End</label>
                                                    <input
                                                        type="time"
                                                        value={slot.endTime.substring(0, 5)}
                                                        onChange={e => handleTimeSlotChange(index, 'endTime', e.target.value + ':00')}
                                                        disabled={isLoading}
                                                        className="form__control"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </>
                    ) : (
                        <p className="text-muted">No time slots added.</p>
                    )}

                    <div className="form__section-actions mt-2">
                        <button
                            type="button"
                            onClick={handleAddTimeSlot}
                            disabled={isLoading}
                            className="btn btn-add-new btn-sm"
                        >
                            Add Time Slot
                        </button>
                    </div>
                </div>
                {!hideSubmitButton && (
                    <div className="form__section-actions mt-2">
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isLoading}
                        >
                            {isEditMode ? 'Update Schedule' : 'Create Schedule'}
                        </button>
                    </div>
                )}
            </form>
        </div>
    );
};
